<script>
  import backend from "~/backend"
  import { writable } from "svelte/store"

  export let chain

  const currentStreak = writable(chain.current_streak)
  const maxStreak = writable(chain.max_streak)

  function persistChanges(chain) {
    const payload = chain.dates.map((date) => {
      return {
        date: date.date,
        completed: date.completed
      }
    })

    backend.updateChain(chain.id, { dates: payload }, (response) => {
      currentStreak.set(response.data.current_streak)
      maxStreak.set(response.data.max_streak)
    })
  }

  let timer
  const debouncedUpdate = (chain) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      persistChanges(chain)
    }, 500)
  }

  $: debouncedUpdate(chain)

  $: streakClass =
    $currentStreak === 0
      ? "text-danger"
      : $currentStreak === $maxStreak
      ? "text-success"
      : "text-fg"
</script>

<main style="--chain: {chain.color || '#5C8D6B'}">
  <div
    class="pl-4 py-3 space-y-2 border-l-4 {chain.dates.every(
      (date) => date.completed
    )
      ? 'border-success'
      : 'border-danger'}"
  >
    <div class="flex items-center">
      <a
        href="/chains/timeline"
        class="chain-title text-sm transition-colors flex-grow min-w-0 truncate"
        data-controller="tooltip"
        data-tooltip-content-value={chain.name}
        data-tooltip-only-when-overflowing-value="true">{chain.name}</a
      >
      <span
        class="shrink-0 text-sm font-semibold transition-colors {streakClass}"
        data-controller="tooltip"
        data-tooltip-content-value="Current streak"
      >
        {$currentStreak}
      </span>
      <span
        class="shrink-0 px-1.5 py-0.5 rounded-full text-xs font-medium text-gray-600"
        data-controller="tooltip"
        data-tooltip-content-value="Longest streak"
      >
        ({$maxStreak})
      </span>
    </div>
    <div class="flex items-center gap-1 md:justify-between md:gap-0.5">
      {#each chain.dates as date (date.id)}
        <div
          class="flex"
          data-controller="tooltip"
          data-tooltip-content-value={date.date}
        >
          <input
            id={date.id}
            type="checkbox"
            class="day-input"
            bind:checked={date.completed}
          />
          <label for={date.id}>
            <span class="day"><span class="day-label">{date.label}</span></span>
          </label>
        </div>
      {/each}
    </div>
  </div>
</main>

<style>
  /* `--chain` is the raw hex off the chain record, used at full strength. */
  main {
    --chain-ring: color-mix(in srgb, var(--chain) 40%, transparent);
    --chain-label: rgb(255 255 255 / 0.92);
    /* Darkened toward `fg` so pale chain colours stay legible as text */
    --chain-text: color-mix(in oklab, var(--chain) 72%, theme("colors.fg"));
  }

  .chain-title:hover {
    color: var(--chain-text);
  }

  .day-input {
    display: none;
  }

  .day {
    @apply relative flex cursor-pointer items-center justify-center;
    @apply w-8 h-8;
    @apply md:w-8 md:h-8;
    @apply lg:w-7 lg:h-7 text-xs;
    @apply xl:w-8 xl:h-8;
    @apply 2xl:w-7 2xl:h-7;
    @apply rounded-full overflow-hidden transition-colors;
    @apply border border-dashed;
    border-color: var(--chain-ring);
  }

  .day:hover {
    border-color: var(--chain);
  }

  /* Fills the circle from the inside out when checked */
  .day:after {
    @apply absolute inset-0 rounded-full transition-transform duration-200;
    content: "";
    background-color: var(--chain);
    transform: scale(0);
  }

  .day-label {
    @apply relative z-10 font-normal text-fg-muted transition-colors;
    @apply text-sm;
    @apply md:text-2xs;
    @apply lg:text-tiny;
    @apply xl:text-xs;
  }

  .day:hover .day-label {
    color: var(--chain);
  }

  .day-input:checked + label .day {
    @apply border-none shadow-sm;
    --tw-shadow-color: var(--chain-ring);
    --tw-shadow: var(--tw-shadow-colored);
  }

  .day-input:checked + label .day:after {
    transform: scale(1);
  }

  .day-input:checked + label .day-label,
  .day-input:checked + label .day:hover .day-label {
    @apply font-light;
    color: var(--chain-label);
  }
</style>
